/**
 * UserStatsStep Component
 * Ortak vücut bilgileri formu - array-based config ile yönetilir
 * FashPlay ve FashSnap tarafından kullanılır
 */

import React, { useState } from 'react';
import { StepHeader } from '../StepHeader';
import './UserStatsStep.css';

export interface UserStatsData {
    age: number;
    height: number;
    weight: number;
}

export interface UserStatsStepProps {
    onComplete: (data: UserStatsData) => void;
    initialData?: Partial<UserStatsData>;
    isLoading?: boolean;
    title?: string;
    subtitle?: string;
}

// ---- Config-driven field definitions ----
interface StatsFieldConfig {
    key: keyof UserStatsData;
    label: string;
    unit?: string;
    min: number;
    max: number;
    defaultValue: number;
    quickValues?: number[];
    showNumberInput?: boolean;
}

const STATS_FIELDS: StatsFieldConfig[] = [
    {
        key: 'age',
        label: 'Yaş',
        min: 18,
        max: 80,
        defaultValue: 25,
        quickValues: [20, 25, 30, 35, 40],
        showNumberInput: false,
    },
    {
        key: 'height',
        label: 'Boy',
        unit: 'cm',
        min: 140,
        max: 210,
        defaultValue: 170,
        showNumberInput: true,
    },
    {
        key: 'weight',
        label: 'Kilo',
        unit: 'kg',
        min: 40,
        max: 150,
        defaultValue: 60,
        showNumberInput: true,
    },
];

export const UserStatsStep: React.FC<UserStatsStepProps> = ({
    onComplete,
    initialData,
    isLoading = false,
    title = 'Vücut Bilgileri',
    subtitle = 'En doğru sonuç için lütfen bilgilerinizi girin.'
}) => {
    const [stats, setStats] = useState<UserStatsData>(() => ({
        age: initialData?.age ?? STATS_FIELDS.find(f => f.key === 'age')!.defaultValue,
        height: initialData?.height ?? STATS_FIELDS.find(f => f.key === 'height')!.defaultValue,
        weight: initialData?.weight ?? STATS_FIELDS.find(f => f.key === 'weight')!.defaultValue,
    }));

    // Update a single field
    const updateField = (key: keyof UserStatsData, value: number) => {
        setStats(prev => ({ ...prev, [key]: value }));
    };

    // Clamp value within min/max
    const clamp = (value: number, min: number, max: number) =>
        Math.min(max, Math.max(min, value));

    // Slider percentage for gradient
    const getPercent = (value: number, min: number, max: number) =>
        ((value - min) / (max - min)) * 100;

    // BMI Calculation
    const bmi = stats.weight / Math.pow(stats.height / 100, 2);
    const getBmiCategory = () => {
        if (bmi < 18.5) return { label: 'Zayıf', color: '#3b82f6' };
        if (bmi < 25) return { label: 'Normal', color: '#22c55e' };
        if (bmi < 30) return { label: 'Biraz Kilolu', color: '#f59e0b' };
        return { label: 'Kilolu', color: '#ef4444' };
    };

    const handleContinue = () => {
        onComplete(stats);
    };

    return (
        <div className="user-stats-step">
            <StepHeader title={title} subtitle={subtitle} />

            <div className="user-stats-step__content">
                {STATS_FIELDS.map((field) => (
                    <div key={field.key} className="stats-input-group">
                        <div className="stats-input-header">
                            <label>{field.label}{field.unit ? ` (${field.unit})` : ''}</label>
                            {field.showNumberInput ? (
                                <div className="stats-value-wrapper">
                                    <input
                                        type="number"
                                        value={stats[field.key]}
                                        onChange={(e) =>
                                            updateField(field.key, clamp(Number(e.target.value), field.min, field.max))
                                        }
                                        className="stats-number-input"
                                    />
                                </div>
                            ) : (
                                <span className="stats-value">{stats[field.key]}</span>
                            )}
                        </div>
                        <input
                            type="range"
                            className="stats-slider"
                            min={field.min}
                            max={field.max}
                            value={stats[field.key]}
                            onChange={(e) => updateField(field.key, Number(e.target.value))}
                            style={{
                                '--percent': `${getPercent(stats[field.key], field.min, field.max)}%`
                            } as React.CSSProperties}
                        />
                        {field.quickValues && (
                            <div className="quick-select">
                                {field.quickValues.map(v => (
                                    <button
                                        key={v}
                                        onClick={() => updateField(field.key, v)}
                                        className={`quick-btn ${stats[field.key] === v ? 'active' : ''}`}
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {/* BMI Indicator */}
                <div className="bmi-indicator">
                    <span>
                        Vücut Kitle İndeksi:{' '}
                        <strong style={{ color: getBmiCategory().color }}>
                            {bmi.toFixed(1)}
                        </strong>{' '}
                        ({getBmiCategory().label})
                    </span>
                </div>
            </div>

            <div className="user-stats-actions">
                <button
                    className={`primary-btn continue-btn ${isLoading ? 'loading' : ''}`}
                    onClick={handleContinue}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            Analiz Ediliyor
                            <span className="spinner"></span>
                        </>
                    ) : (
                        'Devam Et'
                    )}
                </button>
            </div>
        </div>
    );
};

export default UserStatsStep;
